import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationApiService } from '../services/notification-api.service';
import { NotificationService } from '../services/notification.service';
import { UserNotification, notificationBody, notificationTitle } from '../models/notification.model';
import { ToastService } from '../../../shared/services/toast.service';
import { DatetimeService } from '../../../shared/services/datetime.service';

/**
 * Página "Todas las notificaciones" — lista paginada del usuario logueado.
 * Permite filtrar solo no leídas, marcar todas como leídas y navegar a la entidad.
 */
@Component({
  selector: 'app-notification-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-list.component.html',
})
export class NotificationListComponent implements OnInit {
  notifications: UserNotification[] = [];
  loading = false;
  loadError = false;

  // Paginación (wrapper PageResponse propio del backend)
  page = 0;
  size = 20;
  totalElements = 0;
  totalPages = 0;
  hasNext = false;
  hasPrevious = false;

  unreadOnly = false;

  constructor(
    private notificationApi: NotificationApiService,
    private notificationState: NotificationService,
    private datetime: DatetimeService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.notificationApi
      .search({ page: this.page, size: this.size, unreadOnly: this.unreadOnly })
      .subscribe({
        next: (res) => {
          this.notifications = res.content;
          this.page = res.page;
          this.size = res.size;
          this.totalElements = res.totalElements;
          this.totalPages = res.totalPages;
          this.hasNext = res.hasNext;
          this.hasPrevious = res.hasPrevious;
          this.loading = false;
        },
        error: () => {
          this.loadError = true;
          this.loading = false;
        },
      });
  }

  toggleUnreadOnly(value: boolean): void {
    if (this.unreadOnly === value) return;
    this.unreadOnly = value;
    this.page = 0;
    this.load();
  }

  nextPage(): void {
    if (!this.hasNext) return;
    this.page++;
    this.load();
  }

  prevPage(): void {
    if (!this.hasPrevious) return;
    this.page--;
    this.load();
  }

  /** Click en fila: marcar leída (sub-operación, sin toast) y navegar. */
  onNotificationClick(n: UserNotification): void {
    if (!n.isRead) {
      n.isRead = true;
      this.notificationState.decrementUnread();
      this.notificationApi.markRead(n.id).subscribe({
        next: () => {},
        error: () => this.notificationState.refreshUnreadCount(),
      });
      // Si estamos en el filtro "solo no leídas", la fila ya no corresponde.
      if (this.unreadOnly) {
        this.notifications = this.notifications.filter(x => x.id !== n.id);
      }
    }

    const route = this.notificationState.resolveRoute(n);
    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  /** "Marcar todas como leídas" — operación primaria, con toast. */
  markAllAsRead(): void {
    this.notificationApi.markAllRead().subscribe({
      next: (res) => {
        this.notificationState.setUnreadCount(0);
        const message = res.updated === 0
          ? 'No tenías notificaciones sin leer.'
          : res.updated === 1
            ? 'Se marcó 1 notificación como leída.'
            : `Se marcaron ${res.updated} notificaciones como leídas.`;
        this.toast.success({ title: 'Notificaciones', message });
        this.page = 0;
        this.load();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudieron marcar las notificaciones.',
        });
      },
    });
  }

  formatRelative(iso: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `Hace ${diffHrs} h`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return this.datetime.formatDateShort(iso);
  }

  formatFull(iso: string | null): string {
    return this.datetime.formatDateTimeShort(iso);
  }

  /** Título legible: nunca la llave cruda del enum (`TASK_ASSIGNED`). */
  titulo(n: UserNotification): string {
    return notificationTitle(n);
  }

  /** Cuerpo legible, o `null` si no hay nada que mostrar. */
  cuerpo(n: UserNotification): string | null {
    return notificationBody(n);
  }

  trackById(_index: number, n: UserNotification): number {
    return n.id;
  }
}
